import React from 'react';

import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';

import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import type { SearchResultProps } from '../../../../../common/types';
import { useLogException } from '../../../../../controllers/error-boundary/utils';

import type { searchResultRenderer_AtlassianSearchPage$key } from './__generated__/searchResultRenderer_AtlassianSearchPage.graphql';
import { isSearchAssetsObject, SearchAssetsObject } from './search-assets-object';
import {
	isSearchAssetsObjectSchema,
	SearchAssetsObjectSchema,
} from './search-assets-object-schema';
import { isSearchAssetsObjectType, SearchAssetsObjectType } from './search-assets-object-type';
import { isSearchResultAtlasGoal, SearchAtlasGoal } from './search-atlas-goal';
import { isSearchAtlasProject, SearchAtlasProject } from './search-atlas-project';
import { isSearchCompassComponent, SearchCompassComponent } from './search-compass-component';
import {
	isSearchConfluencePageBlogAttachment,
	SearchConfluencePageBlogAttachment,
} from './search-confluence-page-blog-attachment';
import { isSearchConfluenceSpace, SearchConfluenceSpace } from './search-confluence-space';
import { SearchDefaultRenderer } from './search-default-renderer';
import { isSearchFederatedResult, SearchFederatedResult } from './search-federated-result';
import { isSearchGraphResult, SearchGraphResult } from './search-graph-result';
import { isSearchJiraBoard, SearchJiraBoard } from './search-jira-board';
import { isSearchJiraDashboard, SearchJiraDashboard } from './search-jira-dashboard';
import { isSearchJiraFilter, SearchJiraFilter } from './search-jira-filter';
import {
	isSearchJiraIssue,
	type JiraIssueStatusAndColorName,
	SearchJiraIssue,
} from './search-jira-issue';
import { isSearchJiraPlan, SearchJiraPlan } from './search-jira-plan';
import { isSearchJiraProject, SearchJiraProject } from './search-jira-project';
import { isSearchMercuryFocusArea, SearchMercuryFocusArea } from './search-mercury-focus-area';
import {
	isSearchMercuryFocusAreaUpdate,
	SearchMercuryFocusAreaUpdate,
} from './search-mercury-focus-area-update';
import { isSearchPassionfruitAsk, SearchPassionfruitAsk } from './search-passionfruit-ask';
import { isSearchTalentPosition, SearchTalentPosition } from './search-talent-position';
import { isSearchTrello, SearchTrello } from './search-trello';

export type SearchResultRendererProps =
	SearchResultProps<searchResultRenderer_AtlassianSearchPage$key> & {
		thirdPartyConfigs: ThirdPartyConfigsBootstrap;
		compassTeamName?: string | null; // Optional, used for Compass teams
		jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
		jiraLinkedPRs?: IssueLinkedPRsEntry; // Optional, used for Jira issue linked PRs
		is3pGroupingEnabled?: boolean;
		smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
		mainContentWidth?: number;
	};

const SearchResultRenderer = ({
	node: fragmentRef,
	thirdPartyConfigs,
	compassTeamName,
	jiraIssueStatus,
	jiraLinkedPRs,
	smartAnswersPortalRef,
	...props
}: SearchResultRendererProps): React.JSX.Element => {
	// Casting to `any` to avoid type errors with type guards
	// TODO: Improve type guards to avoid casting
	const node: any = useFragment(
		graphql`
			fragment searchResultRenderer_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeIssueTypeField: { type: "Boolean", defaultValue: false }
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
			) {
				__typename
				id
				...searchAtlasGoal_AtlassianSearchPage @arguments(includeIconUrl: $includeIconUrl)
				...searchAtlasProject_AtlassianSearchPage @arguments(includeIconUrl: $includeIconUrl)
				...searchFederatedResult_AtlassianSearchPage
				...searchCompassComponent_AtlassianSearchPage
				...searchConfluencePageBlogAttachment_AtlassianSearchPage
					@arguments(includeIconUrl: $includeIconUrl, includeNavBoostScore: $includeNavBoostScore)
				...searchConfluenceSpace_AtlassianSearchPage @arguments(includeIconUrl: $includeIconUrl)
				...searchGraphResult_AtlassianSearchPage
					@arguments(includeThirdPartyId: $includeThirdPartyId)
				...searchJiraIssue_AtlassianSearchPage
					@arguments(
						includeAssigneeField: $includeAssigneeField
						includeIssueTypeField: $includeIssueTypeField
						includeJiraLiteModeFields: $includeJiraLiteModeFields
					)
				...searchJiraProject_AtlassianSearchPage
					@arguments(includeJiraLiteModeFields: $includeJiraLiteModeFields)
				...searchJiraPlan_AtlassianSearchPage
				...searchJiraBoard_AtlassianSearchPage
				...searchJiraDashboard_AtlassianSearchPage
				...searchJiraFilter_AtlassianSearchPage
				...searchMercuryFocusArea_AtlassianSearchPage
				...searchMercuryFocusAreaUpdate_AtlassianSearchPage
				...searchPassionfruitAsk_AtlassianSearchPage @include(if: $includePassionfruitAsk)
				...searchTrello_AtlassianSearchPage
				...searchAssetsObjectSchema_AtlassianSearchPage @include(if: $includeAssets)
				...searchAssetsObjectType_AtlassianSearchPage @include(if: $includeAssets)
				...searchAssetsObject_AtlassianSearchPage @include(if: $includeAssets)
				...searchDefaultRenderer_AtlassianSearchPage
				...searchTalentPosition_AtlassianSearchPage @include(if: $includePositions)
			}
		`,
		fragmentRef,
	);

	const logException = useLogException();

	// Try/catch because AnyAri.parse throws on malformed ARIs
	try {
		if (!node) {
			throw new Error('ResultRecord: node is undefined');
		}
		if (isSearchConfluenceSpace(node)) {
			return <SearchConfluenceSpace {...props} node={node} />;
		}
		if (isSearchConfluencePageBlogAttachment(node)) {
			return (
				<SearchConfluencePageBlogAttachment
					{...props}
					node={node}
					thirdPartyConfigs={thirdPartyConfigs}
					smartAnswersPortalRef={smartAnswersPortalRef}
				/>
			);
		}
		if (isSearchResultAtlasGoal(node)) {
			return <SearchAtlasGoal {...props} node={node} />;
		}
		if (isSearchAtlasProject(node)) {
			return <SearchAtlasProject {...props} node={node} />;
		}
		if (isSearchCompassComponent(node)) {
			return <SearchCompassComponent {...props} node={node} compassTeamName={compassTeamName} />;
		}
		if (isSearchJiraProject(node)) {
			return <SearchJiraProject {...props} node={node} />;
		}
		if (isSearchJiraIssue(node)) {
			return (
				<SearchJiraIssue
					{...props}
					node={node}
					jiraIssueStatus={jiraIssueStatus}
					jiraLinkedPRs={jiraLinkedPRs}
				/>
			);
		}
		if (isSearchJiraPlan(node)) {
			return <SearchJiraPlan {...props} node={node} />;
		}
		if (isSearchJiraBoard(node)) {
			return <SearchJiraBoard {...props} node={node} />;
		}
		if (isSearchJiraFilter(node)) {
			return <SearchJiraFilter {...props} node={node} />;
		}
		if (isSearchJiraDashboard(node)) {
			return <SearchJiraDashboard {...props} node={node} />;
		}
		if (isSearchTrello(node)) {
			return <SearchTrello {...props} node={node} />;
		}
		if (isSearchMercuryFocusArea(node)) {
			return <SearchMercuryFocusArea {...props} node={node} />;
		}
		if (isSearchMercuryFocusAreaUpdate(node)) {
			return <SearchMercuryFocusAreaUpdate {...props} node={node} />;
		}
		if (isSearchPassionfruitAsk(node) && fg('platform_passionfruit_search_onboarding_enabled')) {
			return <SearchPassionfruitAsk {...props} node={node} />;
		}
		if (isSearchFederatedResult(node)) {
			return <SearchFederatedResult {...props} node={node} thirdPartyConfigs={thirdPartyConfigs} />;
		}
		if (isSearchAssetsObjectSchema(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObjectSchema {...props} node={node} />;
		}
		if (isSearchAssetsObjectType(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObjectType {...props} node={node} />;
		}
		if (isSearchAssetsObject(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObject {...props} node={node} />;
		}
		if (isSearchTalentPosition(node) && fg('talent_global_search')) {
			return <SearchTalentPosition {...props} node={node} />;
		}
		if (node.id && isSearchGraphResult(node)) {
			return <SearchGraphResult {...props} node={node} thirdPartyConfigs={thirdPartyConfigs} />;
		}
		throw new Error(`SearchResultRenderer: unsupported node typename=${node['__typename']}`);
	} catch (e) {
		logException(e);
		return <SearchDefaultRenderer {...props} node={node} />;
	}
};

const SearchResultRendererMemoized = React.memo(SearchResultRenderer);

const SearchResultRendererGated = (props: SearchResultRendererProps): React.JSX.Element => {
	const isOptimisationEnabled = FeatureGates.getExperimentValue<boolean>(
		'rovo_search_page_result_component_optimisations',
		'isEnabled',
		false,
	);

	if (isOptimisationEnabled) {
		return <SearchResultRendererMemoized {...props} />;
	}

	return <SearchResultRenderer {...props} />;
};

export { SearchResultRendererGated as SearchResultRenderer };
